import { useId, useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { requireAuth } from '../features/auth/guards.js';
import { useForm } from '@tanstack/react-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';
import { PageHeader } from '@/components/account/PageHeader.js';
import { StatusPill } from '@/components/account/StatusPill.js';
import { TransactionRow } from '@/components/account/TransactionRow.js';
import { ArrowUpFromLine } from 'lucide-react';
import { BankLogo } from '@/components/account/BankLogo.js';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Badge } from '@/components/ui/badge.js';
import { Icon } from '@/components/ui/icon.js';
import {
  currencyGlyph,
  decimalPlacesFor,
  formatMoney,
  minorFromWire,
  normalizeAmountInput,
  toDecimalString,
  tryToMinorUnits,
} from '@/lib/money/index.js';
import {
  fetchWithdraws,
  fetchWithdrawQuote,
  createWithdraw,
  useBankCurrency,
  useBankMasters,
  useTransferMinimums,
  FINANCE_QUERY_KEYS,
} from '@/features/finance/api.js';
import { bankLabel } from '@/features/finance/bank-label.js';
import { withdrawDestinationBanks } from '@/features/finance/bank-eligibility.js';
import { useFinanceConfig, FINANCE_CONFIG_DEFAULTS } from '@/features/finance/config.js';
import { fetchBankAccounts, MEMBER_QUERY_KEYS } from '@/features/member/api.js';
import { WALLET_QUERY_KEYS, fetchWalletBalances } from '@/features/wallet/api.js';
import { useAuthStore } from '@/stores/auth.js';
import { ApiError, mapApiError } from '@/lib/api/error.js';
import { rateLabel } from '@/lib/money/display-rate.js';
import { useDebouncedValue } from '@/lib/utils/useDebouncedValue.js';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/lib/datetime/format.js';
import { useDisplayMoney } from '@/components/money/MoneyProvider.js';

export const Route = createFileRoute('/_player/wallet/withdraw')({
  beforeLoad: requireAuth,
  component: WithdrawScreen,
});

// Every threshold here is in MINOR units of the member's own currency. An
// earlier version hardcoded two decimals in the pattern and compared
// `parseFloat(v) * 100` against a minor-unit balance, so on a 0-decimal
// currency the input and the balance guard disagreed with each other and with
// the server (WEB-02).

export type WithdrawMessages = {
  invalidAmount: string;
  minAmount: string;
  bankRequired: string;
  exceedsBalance: string;
};

/** i18next's `t`, narrowed to what the copy below actually asks of it. */
type Translate = (key: string, values?: Record<string, unknown>) => string;

/**
 * The form's copy, with the agent's configured floor rendered into the minimum
 * sentence. Split out of the component so the interpolation is checkable
 * without a DOM: the figure shipped as the literal `XXX.XX` placeholder once,
 * and a hint that names no number is worse than none.
 */
export function withdrawMessages(
  t: Translate,
  currency: string,
  minMinor: bigint | null,
): WithdrawMessages {
  return {
    invalidAmount: t('form.invalidAmount'),
    minAmount: t('form.minAmount', {
      amount: minMinor === null ? '' : formatMoney(minMinor, currency),
    }),
    bankRequired: t('form.bankRequired'),
    exceedsBalance: t('form.exceedsBalance'),
  };
}

/**
 * Minor units for a plain decimal in this currency's precision, null for
 * anything else.
 *
 * Never throws. It is reached from a render expression and from a validator that
 * runs on every keystroke, and `safeParse` does not catch a throw out of a
 * refinement — only a ZodError. A typed comma took the whole SPA down (WEB-22).
 */
export function parseAmountMinor(value: string, currency: string): bigint | null {
  const normalized = normalizeAmountInput(value);
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  const parsed = tryToMinorUnits(normalized, currency);
  return parsed.ok ? parsed.minor : null;
}

/** Quote refusals that mean this withdrawal cannot be paid out as typed. */
const UNPAYABLE_QUOTE_CODES = new Set(['FIN-370', 'FIN-371', 'FIN-383', 'FIN-386']);

/**
 * Amount comparisons run in minor units — `parseFloat(v) * 100` overshoots for
 * values like "128.02", which rejected the exact available balance.
 *
 * `minMinor` is the agent's configured withdrawal floor, straight from the
 * resolver svc-finance refuses FIN-307 against. `null` is a real answer, not a
 * missing one: an agent that set no floor accepts any amount, and inventing a
 * client-side default here would be a second minimum that no operator can see
 * or change.
 */
export function buildWithdrawSchema(
  withdrawableMinor: bigint,
  currency: string,
  messages: WithdrawMessages,
  minMinor: bigint | null,
) {
  const dp = decimalPlacesFor(currency);
  const amountPattern = dp === 0 ? /^\d+$/ : new RegExp(`^\\d+(\\.\\d{1,${dp}})?$`);
  return z.object({
    // superRefine, not chained .refine(): a failed `.regex()` marks the parse
    // DIRTY rather than ABORTED, so chained refinements still run on input the
    // regex already rejected. Parsing once and bailing with z.NEVER removes that
    // subtlety (WEB-22).
    amount: z.string().superRefine((raw, ctx) => {
      // A pasted figure brings its surrounding spaces with it far more often
      // than anyone types them, and "500 " refused as malformed is
      // indistinguishable on screen from the floor refusing 500.
      const v = normalizeAmountInput(raw);
      if (!amountPattern.test(v)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: messages.invalidAmount });
        return z.NEVER;
      }
      const minor = parseAmountMinor(v, currency);
      if (minor === null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: messages.invalidAmount });
        return z.NEVER;
      }
      if (minor > withdrawableMinor) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: messages.exceedsBalance });
      }
      if (minMinor !== null && minor < minMinor) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: messages.minAmount });
      }
      return undefined;
    }),
    memberBankId: z.string().uuid(messages.bankRequired),
  });
}

type WithdrawForm = { amount: string; memberBankId: string };

/**
 * Blank amount on purpose. A pre-filled figure reads as a decision the player
 * already made — and the one that shipped (500) sat above most balances, so the
 * submit button quoted money the account did not have.
 */
export const WITHDRAW_FORM_DEFAULTS: WithdrawForm = {
  amount: '',
  memberBankId: '',
};

/**
 * `withdraws.status` is one of pending | approved | completed | rejected |
 * debit_failed | payout_failed. Everything the mapping did not name fell
 * through to 'pending', so an operator's rejection and a failed payout both
 * read as "still being processed" — the player waits for money that is either
 * already back in their wallet or stuck.
 *
 * The return values are PILL names, not wire values, which is why 'paid' is one
 * of them while the column never stores it. 'paid' and 'processing' are still
 * accepted as inputs: the parameter is a bare `string` because it is wire data,
 * and tolerating two aliases costs nothing.
 */
export function withdrawStatusToDisplay(
  status: string,
): 'approved' | 'pending' | 'paid' | 'rejected' | 'failed' {
  if (status === 'completed' || status === 'paid') return 'paid';
  if (status === 'approved' || status === 'processing') return 'approved';
  if (status === 'rejected') return 'rejected';
  if (status === 'debit_failed' || status === 'payout_failed') return 'failed';
  return 'pending';
}

function WithdrawScreen() {
  const { t, i18n } = useTranslation('withdraw');
  const qc = useQueryClient();
  const subject = useAuthStore((s) => s.subject);
  const memberId = subject?.id ?? '';

  const walletQuery = useQuery({
    queryKey: WALLET_QUERY_KEYS.balances,
    queryFn: fetchWalletBalances,
    staleTime: 15_000,
  });
  // A balance that is not whole minor units offers nothing to take out; the
  // server is the authority on what can actually be withdrawn.
  const withdrawable = minorFromWire(walletQuery.data?.withdrawable ?? 0) ?? 0n;
  // Typed in the LEDGER currency — the credits `withdrawable`, the floor and the
  // shortcuts are in. A site showing another currency gets the converted figure
  // beside the box; `currency` stays what the site shows.
  const money = useDisplayMoney();
  const currency = money.currency;
  const ledgerCurrency = money.ledgerCurrency;
  const amountId = useId();
  const convertedId = useId();
  const configQuery = useFinanceConfig();
  const config = configQuery.data ?? FINANCE_CONFIG_DEFAULTS;
  const quickAmounts = useMemo(() => config.withdrawQuickAmounts.map(BigInt), [config.withdrawQuickAmounts]);
  // The agent-level floor. An unresolved read leaves it null and lets the
  // player through to the server's own FIN-307 — a hint that cannot be fetched
  // must not become a refusal the operator never configured. The rules card
  // still distinguishes the two: "not back yet" renders as a placeholder, and
  // only a RESOLVED null says there is no minimum. On a money screen an
  // assertive answer about something unknown is worse than no answer.
  const minimumsQuery = useTransferMinimums();
  const minAmountMinor =
    minimumsQuery.data?.minWithdraw == null ? null : BigInt(minimumsQuery.data.minWithdraw);
  const bankMastersQuery = useBankMasters({ currency: useBankCurrency() });
  const bankByCode = useMemo(
    () => new Map((bankMastersQuery.data?.items ?? []).map((m) => [m.code, m])),
    [bankMastersQuery.data],
  );

  const bankAccountsQuery = useQuery({
    queryKey: MEMBER_QUERY_KEYS.bankAccounts(memberId),
    queryFn: () => fetchBankAccounts(memberId),
    enabled: !!memberId,
    staleTime: 30_000,
  });

  const withdrawsQuery = useQuery({
    queryKey: FINANCE_QUERY_KEYS.withdraws(),
    queryFn: () => fetchWithdraws({ pageSize: 20 }),
    staleTime: 30_000,
  });

  const allBanks = bankAccountsQuery.data?.items ?? [];
  const verifiedBanks = withdrawDestinationBanks(allBanks);
  const defaultBank = verifiedBanks.find((b) => b.isDefault) ?? verifiedBanks[0];

  // With no configured floor, any positive balance can be taken out in full —
  // but never zero, which the server refuses as an invalid amount anyway.
  const canWithdrawAll =
    withdrawable > 0n && withdrawable >= (minAmountMinor ?? 0n);
  const messages = withdrawMessages(t, ledgerCurrency, minAmountMinor);

  const form = useForm<WithdrawForm>({
    defaultValues: {
      ...WITHDRAW_FORM_DEFAULTS,
      memberBankId: defaultBank?.id ?? WITHDRAW_FORM_DEFAULTS.memberBankId,
    },
    onSubmit: ({ value }) => {
      const parsed = buildWithdrawSchema(withdrawable, ledgerCurrency, messages, minAmountMinor).safeParse(value);
      if (!parsed.success) {
        toast.error(parsed.error.errors[0]?.message ?? t('form.validationError'));
        return;
      }
      // Not `mutateAsync`: `onError` already tells the player, and a refusal
      // rethrown through the un-awaited `handleSubmit` is an unhandled rejection.
      withdrawMutation.mutate(value);
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: (values: WithdrawForm) => {
      // Refuse rather than send a figure we could not parse; the throw is
      // contained by the mutation's own onError.
      const typedMinor = parseAmountMinor(values.amount, ledgerCurrency);
      if (typedMinor === null) throw new Error(t('form.invalidAmount'));
      return createWithdraw({
        memberId,
        amount: Number(typedMinor),
        memberBankId: values.memberBankId,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FINANCE_QUERY_KEYS.withdraws() });
      void qc.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balances });
      toast.success(t('form.success'));
      form.reset();
    },
    onError: (err) => {
      toast.error(mapApiError(err, t));
    },
  });

  function handleQuickAmount(minor: bigint) {
    form.setFieldValue('amount', toDecimalString(minor, ledgerCurrency));
    // `setFieldValue` writes the value without running the field's validators,
    // so a refusal left over from an earlier submit outlives the amount it
    // refused. That stale error keeps `canSubmit` false, and `handleSubmit`
    // answers a false `canSubmit` by returning — no request, no toast, nothing
    // on screen but the old red line. Only a reload cleared it.
    void form.validateField('amount', 'change');
  }

  const watchAmount = form.useStore((s) => s.values.amount);
  // The label re-renders on every keystroke against the RAW text the player is
  // typing, so it must never be able to throw — a comma is one character away
  // and there is no route-level boundary to catch it (WEB-22). Unparseable text
  // echoes back rather than formatting a zero, which would read as a real
  // amount.
  const watchAmountMinor = parseAmountMinor(watchAmount || '0', ledgerCurrency);
  const submitAmountLabel =
    watchAmountMinor === null ? (watchAmount ?? '') : formatMoney(watchAmountMinor, ledgerCurrency);
  // An empty field quotes no figure at all. `form.submit` would otherwise read
  // "Request withdrawal · THB 0.00" before the player has typed anything.
  const submitLabel = watchAmount
    ? t('form.submit', { amount: submitAmountLabel })
    : t('form.submitEmpty');

  const typedLedgerMinor = watchAmountMinor ?? 0n;
  const quoteAmount = useDebouncedValue(typedLedgerMinor, 400);
  const quoteQuery = useQuery({
    queryKey: FINANCE_QUERY_KEYS.withdrawQuote(quoteAmount.toString()),
    queryFn: () => fetchWithdrawQuote(quoteAmount),
    enabled: quoteAmount > 0n,
    retry: false,
    staleTime: 60_000,
  });
  const quote = quoteAmount > 0n ? quoteQuery.data : undefined;
  const quoteRefusal =
    quoteAmount > 0n &&
    quoteQuery.error instanceof ApiError &&
    UNPAYABLE_QUOTE_CODES.has(quoteQuery.error.code ?? '')
      ? mapApiError(quoteQuery.error, t)
      : null;
  const quoteLine =
    quote?.payoutAmount != null && quote.payoutCurrency && quote.rate
      ? t('quote.payout', {
          debited: formatMoney(quoteAmount, ledgerCurrency),
          payout: formatMoney(BigInt(quote.payoutAmount), quote.payoutCurrency),
          rate: rateLabel(quote.rate, quote.payoutCurrency, ledgerCurrency),
        })
      : money.rate !== null
        ? t('conversion.rate', { rate: rateLabel(money.rate, currency, ledgerCurrency) })
        : null;
  // A payout account priced at its own rate is what actually arrives; the site
  // rate is only an estimate of it, so the quote replaces it once it is current.
  const convertedLabel =
    quote?.payoutAmount != null &&
    quote.payoutCurrency?.toUpperCase() === currency &&
    quoteAmount === typedLedgerMinor
      ? formatMoney(BigInt(quote.payoutAmount), quote.payoutCurrency)
      : money.format(typedLedgerMinor);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
      />

      <div className="grid lg:grid-cols-2 gap-5">
        {!config.withdrawEnabled ? (
          <Card className="p-8 flex flex-col items-center gap-2 text-center">
            <Icon name="triangle" size={28} className="text-[var(--warning)]" />
            <h3 className="text-[15px] font-bold">{t('unavailable.title')}</h3>
            <p className="text-[13px] text-[var(--text-muted)]">{t('unavailable.body')}</p>
          </Card>
        ) : (
        /* Withdrawal form */
        <Card className="p-5 flex flex-col gap-4">
          <div className="flex items-baseline justify-between">
            <h3 className="text-[15px] font-bold">{t('form.title')}</h3>
            <span className="font-mono text-[11px] text-[var(--text-muted)]">
              {t('available')} ·{' '}
              {walletQuery.isPending ? '…' : (
                <span className="text-[var(--text-secondary)] font-semibold bf-tnum">
                  {formatMoney(withdrawable, ledgerCurrency)}
                </span>
              )}
            </span>
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); form.handleSubmit(); }}
            className="flex flex-col gap-4"
          >
            {/* Amount field */}
            <form.Field name="amount" validators={{ onChange: ({ value }) => {
              const r = buildWithdrawSchema(withdrawable, ledgerCurrency, messages, minAmountMinor)
                .shape.amount.safeParse(value);
              return r.success ? undefined : r.error.errors[0]?.message;
            }}}>
              {(field) => (
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor={amountId}
                    className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]"
                  >
                    {t('form.amount')}
                  </label>
                  <Input
                    id={amountId}
                    prefix={currencyGlyph(ledgerCurrency)}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    inputMode="decimal"
                    placeholder={minAmountMinor === null ? undefined : messages.minAmount}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <span className="text-[12px] font-semibold text-[var(--danger)]">
                      {field.state.meta.errors[0]}
                    </span>
                  )}
                  {money.rate !== null && (
                    <>
                      <label
                        htmlFor={convertedId}
                        className="mt-1 text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]"
                      >
                        {t('conversion.amountIn', { currency })}
                      </label>
                      <output
                        id={convertedId}
                        htmlFor={amountId}
                        aria-live="polite"
                        className="flex items-center h-11 px-3.5 rounded-lg border border-[var(--border)] text-[15px] font-medium text-[var(--text-primary)] bf-tnum"
                      >
                        {convertedLabel}
                      </output>
                    </>
                  )}
                  <div aria-live="polite" className="text-[12px] bf-tnum">
                    {quoteRefusal ? (
                      <span className="font-semibold text-[var(--danger)]">{quoteRefusal}</span>
                    ) : (
                      quoteLine && <span className="text-[var(--text-secondary)]">{quoteLine}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {quickAmounts.map((minor) => {
                      // A shortcut this screen then refuses is a dead control,
                      // and the balance refuses one as surely as the floor
                      // does: a player holding THB 100 was offered 500, 1,000,
                      // 5,000 and 10,000, every one of them a refusal.
                      const belowFloor = minAmountMinor !== null && minor < minAmountMinor;
                      const overBalance = minor > withdrawable;
                      return (
                        <button
                          type="button"
                          key={String(minor)}
                          onClick={() => handleQuickAmount(minor)}
                          disabled={belowFloor || overBalance}
                          className="h-9 px-3.5 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)] hover:border-[var(--primary)] text-[13px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[var(--border)]"
                        >
                          {formatMoney(minor, ledgerCurrency)}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => handleQuickAmount(withdrawable)}
                      disabled={!canWithdrawAll}
                      className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-[var(--primary)]/10 border border-[var(--primary)]/50 hover:bg-[var(--primary)]/20 text-[13px] font-bold text-[var(--primary)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[var(--primary)]/10"
                    >
                      <Icon name="arrowUp" size={14} />
                      {t('form.withdrawAll')}
                      {canWithdrawAll && (
                        <span className="font-mono font-semibold bf-tnum">
                          {formatMoney(withdrawable, ledgerCurrency)}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form.Field>

            {/* Bank selector */}
            <form.Field name="memberBankId" validators={{ onChange: ({ value }) => {
              return value ? undefined : t('form.bankRequired');
            }}}>
              {(field) => (
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
                    {t('form.toBankAccount')}
                  </label>
                  {bankAccountsQuery.isPending ? (
                    <div className="h-12 rounded-lg bg-[var(--bg-elevated)]/50 animate-pulse" />
                  ) : allBanks.length === 0 ? (
                    <div className="text-[12px] text-[var(--text-muted)] py-2">
                      <Link
                        to="/profile"
                        className="text-[var(--primary)] underline underline-offset-2 font-semibold"
                      >
                        {t('form.noBanksCta')}
                      </Link>
                    </div>
                  ) : verifiedBanks.length === 0 ? (
                    <div className="text-[12px] text-[var(--warning)] py-2">
                      {t('form.banksAwaitingVerification')}
                    </div>
                  ) : (
                    <div className="grid gap-2">
                      {verifiedBanks.map((b) => (
                        <button
                          type="button"
                          key={b.id}
                          onClick={() => field.handleChange(b.id)}
                          className={[
                            'flex items-center gap-3 h-12 px-3 rounded-lg border transition-colors text-left',
                            b.id === field.state.value
                              ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                              : 'border-[var(--border)] hover:border-[var(--border-strong)]',
                          ].join(' ')}
                        >
                          <BankLogo code={b.bankCode} size={36} />
                          <div className="flex-1">
                            <div className="text-[13px] font-semibold text-[var(--text-primary)]">{bankLabel(bankByCode.get(b.bankCode) ?? { name: b.bankCode, country: null }, i18n.language)}</div>
                            <div className="font-mono text-[11px] text-[var(--text-muted)]">{b.accountNumber}</div>
                          </div>
                          {b.isDefault && <Badge tone="muted">{t('form.defaultBadge')}</Badge>}
                          {b.id === field.state.value && <Icon name="check" size={16} className="text-[var(--primary)]" />}
                        </button>
                      ))}
                    </div>
                  )}
                  {field.state.meta.errors.length > 0 && (
                    <span className="text-[12px] font-semibold text-[var(--danger)]">
                      {field.state.meta.errors[0]}
                    </span>
                  )}
                </div>
              )}
            </form.Field>

            {/* Rules */}
            <div className="rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)] p-3 text-[12px] text-[var(--text-secondary)] grid grid-cols-2 gap-y-1">
              <span>{t('rules.minimum')}</span>
              <span className="text-right font-semibold text-[var(--text-primary)] bf-tnum">
                {minimumsQuery.isPending
                  ? '…'
                  : minAmountMinor === null
                    ? t('rules.noMinimum')
                    : formatMoney(minAmountMinor, ledgerCurrency)}
              </span>
              <span>{t('rules.fee')}</span>
              <span className="text-right font-semibold text-[var(--text-primary)]">{t('rules.feeValue')}</span>
              <span>{t('rules.time')}</span>
              <span className="text-right font-semibold text-[var(--text-primary)]">{t('rules.timeValue')}</span>
            </div>

            <Button
              type="submit"
              size="lg"
              glow
              fullWidth
              disabled={withdrawMutation.isPending || verifiedBanks.length === 0 || quoteRefusal !== null}
            >
              {withdrawMutation.isPending ? t('form.submitting') : submitLabel}
            </Button>
          </form>
        </Card>
        )}

        {/* Policy card */}
        <div className="flex flex-col gap-4">
          <Card className="p-5">
            <h3 className="text-[15px] font-bold mb-3">{t('policy.title')}</h3>
            <ul className="flex flex-col gap-2 text-[13px] text-[var(--text-secondary)]">
              {[
                t('policy.item1'),
                t('policy.item2'),
                t('policy.item3'),
                t('policy.item4'),
              ].map((item, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[var(--primary)] mt-0.5 shrink-0">·</span>
                  {item}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>

      {/* Recent withdrawals */}
      <section className="flex flex-col gap-3">
        <h2 className="text-[15px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
          {t('recent.title')}
        </h2>
        {withdrawsQuery.isPending ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-14 rounded-xl bg-[var(--bg-elevated)]/50 animate-pulse" />
            ))}
          </div>
        ) : withdrawsQuery.data?.items.length === 0 ? (
          <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-raised)] py-8 text-center text-[var(--text-muted)] text-[13px]">
            {t('recent.empty')}
          </div>
        ) : (
          <Card className="overflow-hidden">
            {(withdrawsQuery.data?.items ?? []).map((w, i) => {
              // Paid out in another currency: the payout is the headline, the
              // ledger debit and the rate fixed at request time sit beneath it.
              const paidIn = w.payoutCurrency && w.payoutAmount != null ? w.payoutCurrency : null;
              return (
                <TransactionRow
                  key={w.id}
                  icon={ArrowUpFromLine}
                  title={t('recent.channel')}
                  badge={<StatusPill status={withdrawStatusToDisplay(w.status)} />}
                  subtitle={`${w.id.slice(0, 8)} · ${formatDateTime(w.createdAt, i18n.language)}${
                    w.rejectReason ? ` · ${w.rejectReason}` : ''
                  }`}
                  note={
                    paidIn ? (
                      <span className="text-[var(--text-muted)] bf-tnum">
                        {t('recent.debited', { amount: formatMoney(w.amount, ledgerCurrency) })}
                        {w.convertRate ? ` · ${rateLabel(w.convertRate, paidIn, ledgerCurrency)}` : ''}
                      </span>
                    ) : undefined
                  }
                  amountMinor={paidIn ? (w.payoutAmount ?? 0) : w.amount}
                  currency={paidIn ?? ledgerCurrency}
                  tone="debit"
                  hasBorder={i > 0}
                />
              );
            })}
          </Card>
        )}
      </section>
    </div>
  );
}
